import { type MenuItem } from '../../../mocks/menuData';

interface MenuItemModalProps {
  item: MenuItem | null;
  onClose: () => void;
}

const tagConfig: Record<string, { label: string; color: string; icon: string }> = {
  vegetarian: { label: 'Vegetarian', color: 'bg-green-100 text-green-700', icon: 'ri-leaf-line' },
  vegan: { label: 'Vegan', color: 'bg-green-100 text-green-700', icon: 'ri-plant-line' },
  'non-veg': { label: 'Non-Veg', color: 'bg-red-100 text-red-700', icon: 'ri-knife-line' },
  spicy: { label: 'Spicy', color: 'bg-orange-100 text-orange-700', icon: 'ri-fire-line' },
  'gluten-free': { label: 'Gluten Free', color: 'bg-blue-100 text-blue-700', icon: 'ri-shield-check-line' },
};

export default function MenuItemModal({ item, onClose }: MenuItemModalProps) {
  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-cream rounded-3xl w-full max-w-lg overflow-hidden" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Image */}
        {item.image && (
          <div className="relative h-56 sm:h-72 overflow-hidden">
            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-6 sm:p-8">
          {!item.image && (
            <div className="flex justify-end mb-2">
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-charcoal/10 text-charcoal hover:bg-charcoal/20 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          <div className="flex items-start justify-between gap-4 mb-3">
            <h2 className="font-serif text-3xl text-charcoal flex-1">{item.name}</h2>
            <div className="text-right">
              <p className="font-sans font-bold text-2xl text-teal whitespace-nowrap">
                {item.priceFrom ? `From ${item.price}` : item.price}
              </p>
            </div>
          </div>

          <p className="font-sans text-sm text-charcoal/70 leading-relaxed mb-5">{item.description}</p>

          {/* Protein options */}
          {item.proteins && (
            <div className="mb-5">
              <p className="font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">Choose Your Protein</p>
              <div className="flex flex-wrap gap-2">
                {item.proteins.map((p) => (
                  <span key={p} className="font-sans text-xs px-3 py-1.5 rounded-full bg-saffron/10 text-saffron font-medium border border-saffron/20">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Spice Level */}
          {item.spiceLevel > 0 && (
            <div className="mb-5">
              <p className="font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">Spice Level</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((level) => (
                  <i
                    key={level}
                    className={`ri-fire-fill text-lg ${level <= item.spiceLevel ? 'text-coral' : 'text-charcoal/15'}`}
                  ></i>
                ))}
                <span className="font-sans text-xs text-charcoal/50 ml-2">
                  {item.spiceLevel === 1 ? 'Mild' : item.spiceLevel === 2 ? 'Medium' : item.spiceLevel === 3 ? 'Medium Hot' : item.spiceLevel === 4 ? 'Hot' : 'Extra Hot'}
                </span>
              </div>
            </div>
          )}

          {/* Tags */}
          {item.tags.length > 0 && (
            <div className="mb-6">
              <p className="font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">Dietary</p>
              <div className="flex flex-wrap gap-2">
                {item.tags.filter(t => t !== 'spicy').map((tag) => {
                  const cfg = tagConfig[tag];
                  if (!cfg) return null;
                  return (
                    <span key={tag} className={`font-sans text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1 ${cfg.color}`}>
                      <i className={cfg.icon}></i>
                      {cfg.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-saffron to-coral text-white font-sans font-semibold text-sm py-3.5 rounded-full hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
